import dayjs from 'dayjs';
import { type ApplicantDetailed } from '../API/GET/applicant/response';
import { type Requirement } from '../API/GET/client/carePlan/requirement/response';
import { type CarePlanDetailed } from '../API/GET/client/carePlan/response';
import { type ClientDetailed } from '../API/GET/client/response';
import {
  type CreateClientCarePlan,
  type CreateClient,
} from '../API/POST/client/request';
import { type UpdateCarePlan } from '../API/PUT/client/carePlan/request';
import { type UpdateRequirement } from '../API/PUT/client/carePlan/requirement/request';
import { type UpdateClient } from '../API/PUT/client/request';
import omit from 'lodash/omit';

export function clientDetailToUpdateClient(
  clientDetail: ClientDetailed
): UpdateClient {
  return {
    firstName: clientDetail.firstName,
    lastName: clientDetail.lastName,
    title: clientDetail.title,
    sex: clientDetail.sex,
    birthdate: clientDetail.birthdate,
    phone: clientDetail.phone,
    email: clientDetail.email,
    contacts: clientDetail.contacts.map(c => ({ ...c, title: c.title ?? '' })),
    servAddress: clientDetail.servAddress,
    permAddress: clientDetail.permAddress,
    tags: clientDetail.tags.map(t => t.id),
  };
}

export function applicantToCreateClient(
  applicant: ApplicantDetailed
): CreateClient {
  return {
    firstName: applicant.firstName,
    lastName: applicant.lastName,
    title: applicant.title,
    sex: applicant.sex,
    birthdate: applicant.birthdate,
    phone: undefined,
    email: undefined,
    contacts: applicant.contacts,
    servAddress: applicant.servAddress,
    permAddress: applicant.permAddress,
    serviceId: applicant.serviceId,
    tags: [],
    applicantData: {
      careReq: applicant.careReq,
      statusDesc: applicant.statusDesc,
      note: applicant.note,
      keyEmp: applicant.keyEmp.id,
    },
  };
}

export function appllicantToCreateClientCarePlan(
  applicant: ApplicantDetailed
): CreateClientCarePlan {
  return {
    serviceId: applicant.serviceId,
    applicantId: applicant.id,
    healthDesc: applicant.statusDesc,
    note: applicant.note ?? undefined,
    lastRevDate: dayjs().format('YYYY-MM-DD'),
    nextRevDate: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    expDate: dayjs().add(1, 'year').format('YYYY-MM-DD'),
  };
}

export function carePlanToUpdateCarePlan(
  carePlan: CarePlanDetailed
): UpdateCarePlan {
  return {
    serviceId: carePlan.serviceId,
    applicantId: carePlan.applicantId,
    mobility: carePlan.mobility,
    goal: carePlan.goal,
    healthDesc: carePlan.healthDesc,
    sitDesc: carePlan.sitDesc,
    risks: carePlan.risks,
    reqCare: carePlan.reqCare,
    note: carePlan.note,
    noteCoord: carePlan.noteCoord,
    noteCaregiver: carePlan.noteCaregiver,
    clientRes: carePlan.clientRes,
    lastRevDate: carePlan.lastRevDate,
    nextRevDate: carePlan.nextRevDate,
    expDate: carePlan.expDate,
    keyEmpId: carePlan.keyEmp?.id,
  };
}

export function requirementToUpdateRequirement(
  requirement: Requirement
): UpdateRequirement {
  return {
    name: requirement.name,
    duration: requirement.duration,
    startTime: requirement.startTime,
    boundStartTime: requirement.boundStartTime,
    boundEndTime: requirement.boundEndTime,
    startDate: requirement.startDate,
    note: requirement.note,
    prefSex: requirement.prefSex,
    recur: requirement.recur,
    tags: requirement.tags === null ? [] : requirement.tags.map(r => r.id),
  };
}

/**
 * Merges the properties of an UpdateClient object into a ClientDetailed object.
 *
 * This function is used to update the client details in the cache after a successful update.
 *
 * @param clientDetail The original ClientDetailed object.
 * @param updateClient The UpdateClient object containing the updated properties.
 * @returns A new ClientDetailed object with the merged properties.
 */
export function clientDetailedMergeUpdateClient(
  clientDetail: ClientDetailed,
  updateClient: UpdateClient
): ClientDetailed {
  return { ...clientDetail, ...omit(updateClient, ['tags']) };
}
